import { recordedName, renamed } from './targetLabel';
import { type AuditMessage, type AuditText, auditMessage, joinAuditTexts } from './text';
import type {
    AuditLogEntry,
    AuditLogFilters,
    AuditScopeKind,
    AuditScopeRef,
    AuditScopeState,
} from './types';

/** One step of the "Client A › Site North › Pen 5" path under a target. */
export interface AuditScopeSegment {
    kind: AuditScopeKind;
    id: number;
    name: AuditText;
    state: AuditScopeState;
    /** "(archived)", "(deleted)" or "(historical)"; null when active or missing. */
    stateLabel: AuditMessage | null;
    /** "Not found" when the id resolves to no row. */
    title: AuditText | null;
    filter: AuditLogFilters;
}

export interface AuditTargetDescription {
    /** The entity type word, with the raw type as its fallback. */
    type: AuditMessage;
    /** Null when the type alone says it all, as for the audit log itself. */
    name: AuditText | null;
    /** The label written with the entry, when the name shows only its own part of it. */
    recordedLabel: string | null;
    stateLabel: AuditMessage | null;
    title: AuditText | null;
    /** Set when the name recorded with the entry differs from the current one. */
    renamed: { marker: AuditMessage; title: AuditMessage } | null;
    /** The entry's scope, leaving out the level that is the target itself. */
    path: AuditScopeSegment[];
    filter: AuditLogFilters;
}

const SCOPE_ID_KEY: Record<AuditScopeKind, string> = {
    client: 'auditLog.target.clientId',
    location: 'auditLog.target.locationId',
    cage: 'auditLog.target.cageId',
};

/** "Client #12": the raw form, for a row that is gone or a client without a name. */
export function scopeIdText(kind: AuditScopeKind, id: number): AuditMessage {
    return auditMessage(SCOPE_ID_KEY[kind], { id: String(id) });
}

export function scopeStateLabel(state: AuditScopeState): AuditMessage | null {
    return state === 'active' || state === 'missing'
        ? null
        : auditMessage(`auditLog.scopeState.${state}`);
}

function notFoundTitle(ref: AuditScopeRef): AuditMessage | null {
    return ref.state === 'missing' ? auditMessage('auditLog.target.notFound') : null;
}

/** The filter a click on one scope level sets. */
export function scopeFilter(kind: AuditScopeKind, id: number): AuditLogFilters {
    switch (kind) {
        case 'client':
            return { clientId: id };
        case 'location':
            return { locationId: id };
        case 'cage':
            return { cageId: id };
    }
}

interface ScopeLevel {
    kind: AuditScopeKind;
    ref: AuditScopeRef;
}

function scopeLevels(entry: AuditLogEntry): ScopeLevel[] {
    const levels: ScopeLevel[] = [];
    if (entry.client !== null) levels.push({ kind: 'client', ref: entry.client });
    if (entry.location !== null) levels.push({ kind: 'location', ref: entry.location });
    if (entry.cage !== null) levels.push({ kind: 'cage', ref: entry.cage });
    return levels;
}

function segment({ kind, ref }: ScopeLevel): AuditScopeSegment {
    return {
        kind,
        id: ref.id,
        name: ref.name ?? scopeIdText(kind, ref.id),
        state: ref.state,
        stateLabel: scopeStateLabel(ref.state),
        title: notFoundTitle(ref),
        filter: scopeFilter(kind, ref.id),
    };
}

function withState(name: AuditText, state: AuditText | null): AuditText {
    return state === null ? name : auditMessage('auditLog.chips.value.withState', { name, state });
}

/** "Site North (deleted)": a path segment as the chip its click adds names it. */
export function scopeSegmentText(segment: AuditScopeSegment): AuditText {
    return withState(segment.name, segment.stateLabel);
}

/**
 * The whole path for the line's title, where the ellipsis cannot hide a state: each
 * segment with its state, and "Not found" for a row that is gone.
 */
export function scopePathTitle(path: readonly AuditScopeSegment[]): AuditText {
    const joined = joinAuditTexts(
        path.map((segment) => withState(scopeSegmentText(segment), segment.title))
    );
    // Names are looked up when the log is read, not when the entry was written.
    return path.length === 0
        ? joined
        : auditMessage('auditLog.target.currentNames', { path: joined });
}

function targetFilter(entry: AuditLogEntry): AuditLogFilters {
    return entry.entityId === null
        ? { entityType: [entry.entityType] }
        : { entityType: [entry.entityType], entityId: entry.entityId };
}

/**
 * A client, site or pen target is named as it is now, since only those have a read-time
 * lookup; every other type shows its own part of the label written with the entry. A
 * failed login's label is the attempted email, so the row names the attempt, not an
 * account.
 */
export function describeAuditTarget(entry: AuditLogEntry): AuditTargetDescription {
    const levels = scopeLevels(entry);
    const own = levels.find(
        ({ kind, ref }) => kind === entry.entityType && entry.entityId === String(ref.id)
    );
    const base = {
        type: auditMessage(`auditLog.entityType.${entry.entityType}`, undefined, entry.entityType),
        path: levels.filter((level) => level !== own).map(segment),
        filter: targetFilter(entry),
    };
    if (own === undefined) {
        return {
            ...base,
            ...(entry.entityType === 'audit_log'
                ? { name: null, recordedLabel: null }
                : recordedName(entry)),
            stateLabel: null,
            title: null,
            renamed: null,
        };
    }
    const found = own.ref.state !== 'missing' && own.ref.name !== null;
    return {
        ...base,
        ...(found ? { name: own.ref.name, recordedLabel: null } : recordedName(entry)),
        stateLabel: scopeStateLabel(own.ref.state),
        title: notFoundTitle(own.ref),
        renamed: found ? renamed(entry, own.kind, own.ref) : null,
    };
}
